import moment from 'moment';
const SITE_LOCALE_LANGUAGE = 'es-CL';
const CHILE_TIMEZONE = 'America/Santiago';
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const isDateOnlyString = (value: string) => DATE_ONLY_REGEX.test(value);

const parseDateForChile = (dateString: string): Date | null => {
  if (!dateString) return null;

  if (isDateOnlyString(dateString)) {
    const date = new Date(`${dateString}T12:00:00Z`);
    return isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

export const validateDateFromToday = (date: Date): boolean => {
  const today = moment().format('YYYY-MM-DD');
  const jobDate = moment(date).format('YYYY-MM-DD');
  return moment(today).isSameOrBefore(jobDate);
};

export function dateFormatterString(
  dateString: string,
  locales = 'es-ES'
): string {
  try {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    const date: Date = new Date(dateString);
    if (isValidDate(date)) {
      const newDateString: string = date.toLocaleDateString(locales, options);
      return newDateString;
    }

    const isValidDateFormat: boolean = moment(
      dateString,
      'DD-MM-YYYY HH:mm:ss'
    ).isValid();
    if (isValidDateFormat) {
      const newformattedDate: string = moment(
        dateString,
        'DD-MM-YYYY HH:mm:ss'
      ).format('YYYY-MM-DD HH:mm:ss');
      const dateFormat: Date = new Date(newformattedDate);
      const newDateString: string = dateFormat.toLocaleDateString(
        locales,
        options
      );
      return newDateString;
    }

    return dateString;
  } catch (e) {
    // this.loggerUtil.error(e, '', 'dateFormatterString()');
    return dateString;
  }
}

const isValidDate = (d: Date): boolean => {
  return d instanceof Date && isFinite(d.getTime());
};

export const formatTimeMilliseconds = (milliseconds: number) => {
  const seconds = Math.floor((milliseconds / 1000) % 60);
  const minutes = Math.floor((milliseconds / 1000 / 60) % 60);
  const hours = Math.floor((milliseconds / 1000 / 60 / 60) % 24);

  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0')
  ].join(':');
};

const adjustTime = (time: number, length: number, fillChar: string) =>
  Math.abs(time).toString().padStart(length, fillChar);

// Function to obtain the currently date in format: AAAA-MM-DD HH:MM:SS +/-HH:MM
export const getTimezoneDate = () => {
  const now = new Date();
  const adjustedDate = new Date(
    now.getTime() - now.getTimezoneOffset() * 60000
  );

  const [datePart, timePartInitial] = adjustedDate.toISOString().split('T');
  let timePart = timePartInitial;
  timePart = timePart.split('.')[0];

  const timezoneOffsetMinutes = now.getTimezoneOffset();
  const timezoneOffsetHours = Math.floor(timezoneOffsetMinutes / 60);
  const timezoneOffsetMinutesExtra = timezoneOffsetMinutes % 60;
  const timezoneOffsetSymbol = now.getTimezoneOffset() <= 0 ? '+' : '-';
  const timezoneOffset =
    adjustTime(timezoneOffsetHours, 2, '0') +
    ':' +
    adjustTime(timezoneOffsetMinutesExtra, 2, '0');
  return `${datePart}T${timePart}${timezoneOffsetSymbol}${timezoneOffset}`;
};

const getMonth = ({
  dateFormat = SITE_LOCALE_LANGUAGE,
  dateString,
  responseFormat
}: {
  dateFormat?: string;
  dateString: string;
  responseFormat: 'long' | 'short';
}) => {
  if (!dateString) return '';
  const date = parseDateForChile(dateString);
  if (!date) return '';

  return new Intl.DateTimeFormat(dateFormat, {
    month: responseFormat,
    timeZone: CHILE_TIMEZONE
  }).format(date);
};

const getDay = ({
  dateFormat = SITE_LOCALE_LANGUAGE,
  dateString,
  responseFormat
}: {
  dateFormat?: string;
  dateString: string;
  responseFormat: 'numeric' | '2-digit';
}) => {
  if (!dateString) return '';

  if (isDateOnlyString(dateString)) {
    const day = dateString.slice(8, 10);
    if (responseFormat === 'numeric') return String(Number(day));
    return day;
  }

  const date = parseDateForChile(dateString);

  if (!date) return '';

  return new Intl.DateTimeFormat(dateFormat, {
    day: responseFormat,
    timeZone: CHILE_TIMEZONE
  }).format(date);
};

export { getDay, getMonth };
